/**
 * TypeScript mirror of `tokens.scss`. Use these constants for inline
 * styles, SVG fills, chart palettes, or anywhere CSS variables don't
 * reach (canvas, Sentry config, JSON exports, etc.).
 *
 * The CSS variables are the runtime source of truth. These values
 * mirror them and must be kept in sync with `tokens.scss`.
 *
 * NOTE: this mirror holds the **light** Sharp palette only — a plain JS
 * object can't react to `[data-theme="dark"]`. Call sites that need to be
 * theme-aware are migrated to read CSS variables (or a theme hook) in later
 * redesign steps; until then JS-driven fills use these light values.
 *
 * One canonical name per color value — no duplicate aliases. Consumers read
 * the Sharp keys directly (text, loss, onBrand, white, …).
 */

export const colors = {
  // --- Sharp palette (light) — mirrors the prototype app/styles.css ----------
  brand: "#21cf5b",
  brandInk: "#0c6e34",
  brandTint: "#e7f7ed",
  onBrand: "#053216",
  brandStrong: "#18a849",
  brandBright: "#2ae265",

  loss: "#d63842",
  lossTint: "#fcebec",

  pos: "#0c8a3e",
  posTint: "#e7f7ed",

  live: "#d97a16",
  liveTint: "#fbeede",

  info: "#2b6fd1",
  infoTint: "#e6eefb",

  draw: "#898f98",
  gold: "#c79a2e",

  page: "#f4f4ef",
  surface: "#ffffff",
  surface2: "#fbfbf8",
  surfaceSunk: "#eeeee8",
  border: "#e6e6df",
  borderStrong: "#d6d6cc",
  divider: "#edede6",
  hover: "rgba(20, 22, 26, 0.045)",

  text: "#15171b",
  text2: "#565b63",
  // Mirrors tokens.scss light --color-text-3 (AA-compliant muted text).
  text3: "#656b74",
  text4: "#aeb3ba",

  overlay: "rgba(0, 0, 0, 0.5)",
  overlayStrong: "rgba(25, 25, 36, 0.24)",
  white: "#ffffff",
  black: "#000000",
} as const;

export const radius = {
  none: 0,
  xs: 5,
  sm: 8,
  md: 11,
  lg: 16,
  xl: 22,
  full: 999,
} as const;

/** Breakpoint values in pixels. Mirrors the `$breakpoint-*` Sass vars. */
export const breakpoints = {
  xs: 480,
  sm: 640,
  md: 768,
  lg: 1024,
  /** Canonical "is mobile or tablet" cutoff. Drives `useIsMobileOrTablet`. */
  xl: 1366,
  "2xl": 1536,
} as const;

export const zIndex = {
  base: 0,
  raised: 10,
  dropdown: 20,
  sticky: 30,
  overlay: 50,
  modal: 100,
  tooltip: 200,
  toast: 1000,
} as const;

export type ColorToken = keyof typeof colors;
export type BreakpointToken = keyof typeof breakpoints;
